// Atlas Map Service - Handles map block generation with img2img
import { supabase } from './supabase';
import { AtlasMapBlock, Location } from '../types';
import { generateImage } from './imageGeneration';
import { uploadToR2 } from './storage';

// Constants for block generation
const BLOCK_SIZE = 200; // Default block size in pixels

// Location images are flexible in style - user's description drives the aesthetic
const LOCATION_PROMPT_SUFFIX = ', overhead view, bird\'s eye view, top-down perspective, detailed illustration';

// Map expansion blocks are specifically black and white terrain maps
const BLOCK_PROMPT_SUFFIX = ', overhead view, bird\'s eye view, top-down perspective, black and white, monochrome, map style, detailed terrain, seamless edges';

export const AtlasMapService = {
    /**
     * Get all map blocks for an idea
     */
    getMapBlocks: async (ideaId: string): Promise<AtlasMapBlock[]> => {
        const { data, error } = await supabase
            .from('atlas_map_blocks')
            .select('*')
            .eq('idea_id', ideaId)
            .order('created_at', { ascending: true });

        if (error) {
            console.error('Error fetching map blocks:', error);
            return [];
        }
        return data || [];
    },

    /**
     * Generate a location image (txt2img for initial location)
     * Uses flexible styling based on user's description
     */
    generateLocationImage: async (
        description: string,
        userId: string,
        locationId: string
    ): Promise<string | null> => {
        try {
            const prompt = description + LOCATION_PROMPT_SUFFIX;

            // Generate image using prunaai/z-image-turbo
            const result = await generateImage({
                prompt,
                model: 'prunaai/z-image-turbo',
                aspectRatio: '1:1'
            });

            if (!result.imageUrl && !result.imageBlob) {
                console.error('Image generation failed: No image returned');
                return null;
            }

            // Upload to R2
            let finalUrl: string;
            if (result.imageBlob) {
                const file = new File([result.imageBlob], `${locationId}.png`, { type: 'image/png' });
                finalUrl = await uploadToR2(file, `atlas/${userId}`, userId);
            } else if (result.imageUrl?.startsWith('blob:')) {
                const response = await fetch(result.imageUrl);
                const blob = await response.blob();
                const file = new File([blob], `${locationId}.png`, { type: 'image/png' });
                finalUrl = await uploadToR2(file, `atlas/${userId}`, userId);
            } else {
                finalUrl = result.imageUrl!;
            }

            return finalUrl;
        } catch (error) {
            console.error('Error generating location image:', error);
            return null;
        }
    },

    /**
     * Generate a map block using img2img from adjacent blocks
     */
    generateBlock: async (
        ideaId: string,
        x: number,
        y: number,
        adjacentBlocks: (Location | AtlasMapBlock)[]
    ): Promise<AtlasMapBlock | null> => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
            console.error('No authenticated user');
            return null;
        }

        try {
            // Determine the composite image layout based on adjacent blocks
            const compositeData = await createCompositeImage(x, y, adjacentBlocks);
            if (!compositeData) {
                console.error('Failed to create composite image');
                return null;
            }

            // Build prompt from adjacent block descriptions
            const descriptions = adjacentBlocks
                .map(block => {
                    if ('description' in block && block.description) {
                        return block.description;
                    }
                    if ('prompt' in block && block.prompt) {
                        return block.prompt;
                    }
                    if ('name' in block) {
                        return block.name;
                    }
                    return 'terrain';
                })
                .join(', ');

            const prompt = `seamless terrain continuation of ${descriptions}${BLOCK_PROMPT_SUFFIX}`;

            // Generate using img2img
            // Map aspect ratio to valid API values
            const apiAspectRatio = compositeData.aspectRatio === '3:2' ? '3:2' :
                                   compositeData.aspectRatio === '2:3' ? '2:3' : '1:1';
            const result = await generateImage({
                prompt,
                model: 'prunaai/z-image-turbo-img2img',
                aspectRatio: apiAspectRatio as '1:1' | '3:2' | '2:3',
                inputImage: compositeData.imageUrl
            });

            if (!result.imageUrl && !result.imageBlob) {
                console.error('Map block generation failed: No image returned');
                return null;
            }

            // Get the image URL
            let generatedImageUrl: string;
            if (result.imageBlob) {
                generatedImageUrl = URL.createObjectURL(result.imageBlob);
            } else {
                generatedImageUrl = result.imageUrl!;
            }

            // Extract the new block portion from the generated image
            const newBlockUrl = await extractBlockFromComposite(
                generatedImageUrl,
                compositeData.newBlockPosition,
                user.id,
                ideaId
            );

            if (!newBlockUrl) {
                console.error('Failed to extract new block from composite');
                return null;
            }

            // Save to database
            const sourceIds = adjacentBlocks.map(b => b.id);
            const { data, error } = await supabase
                .from('atlas_map_blocks')
                .insert({
                    idea_id: ideaId,
                    user_id: user.id,
                    canvas_x: x,
                    canvas_y: y,
                    width: BLOCK_SIZE,
                    height: BLOCK_SIZE,
                    image_url: newBlockUrl,
                    source_block_ids: sourceIds,
                    prompt
                })
                .select()
                .single();

            if (error) {
                console.error('Error saving map block:', error);
                return null;
            }

            return data;
        } catch (error) {
            console.error('Error generating map block:', error);
            return null;
        }
    },

    /**
     * Delete a map block
     */
    deleteMapBlock: async (id: string): Promise<boolean> => {
        const { error } = await supabase
            .from('atlas_map_blocks')
            .delete()
            .eq('id', id);

        if (error) {
            console.error('Error deleting map block:', error);
            return false;
        }
        return true;
    },

    /**
     * Find adjacent blocks/locations for a given position
     */
    findAdjacentBlocks: (
        x: number,
        y: number,
        locations: Location[],
        mapBlocks: AtlasMapBlock[],
        blockSize: number = BLOCK_SIZE
    ): (Location | AtlasMapBlock)[] => {
        const adjacent: (Location | AtlasMapBlock)[] = [];
        const threshold = blockSize * 1.1; // Allow small overlap

        // Check all 4 directions
        const directions = [
            { dx: -blockSize, dy: 0 },  // left
            { dx: blockSize, dy: 0 },   // right
            { dx: 0, dy: -blockSize },  // top
            { dx: 0, dy: blockSize }    // bottom
        ];

        for (const { dx, dy } of directions) {
            const checkX = x + dx;
            const checkY = y + dy;

            // Check locations
            for (const loc of locations) {
                if (loc.canvas_x != null && loc.canvas_y != null && loc.atlas_image_url) {
                    const locCenterX = loc.canvas_x + (loc.canvas_width || BLOCK_SIZE) / 2;
                    const locCenterY = loc.canvas_y + (loc.canvas_height || BLOCK_SIZE) / 2;
                    const targetCenterX = checkX + blockSize / 2;
                    const targetCenterY = checkY + blockSize / 2;

                    const distance = Math.sqrt(
                        Math.pow(locCenterX - targetCenterX, 2) +
                        Math.pow(locCenterY - targetCenterY, 2)
                    );

                    if (distance < threshold) {
                        adjacent.push(loc);
                    }
                }
            }

            // Check map blocks
            for (const block of mapBlocks) {
                const blockCenterX = block.canvas_x + block.width / 2;
                const blockCenterY = block.canvas_y + block.height / 2;
                const targetCenterX = checkX + blockSize / 2;
                const targetCenterY = checkY + blockSize / 2;

                const distance = Math.sqrt(
                    Math.pow(blockCenterX - targetCenterX, 2) +
                    Math.pow(blockCenterY - targetCenterY, 2)
                );

                if (distance < threshold) {
                    adjacent.push(block);
                }
            }
        }

        return adjacent;
    },

    /**
     * Check if a position is valid for placing a new block (must be adjacent to existing content)
     */
    isValidBlockPosition: (
        x: number,
        y: number,
        locations: Location[],
        mapBlocks: AtlasMapBlock[],
        blockSize: number = BLOCK_SIZE
    ): boolean => {
        const adjacent = AtlasMapService.findAdjacentBlocks(x, y, locations, mapBlocks, blockSize);
        return adjacent.length > 0;
    },

    /**
     * Detect L-shape configuration for 3->4 block generation
     */
    detectLShape: (
        x: number,
        y: number,
        locations: Location[],
        mapBlocks: AtlasMapBlock[],
        blockSize: number = BLOCK_SIZE
    ): { blocks: (Location | AtlasMapBlock)[]; type: 'L' | 'line' | 'single' } => {
        const adjacent = AtlasMapService.findAdjacentBlocks(x, y, locations, mapBlocks, blockSize);

        if (adjacent.length === 0) {
            return { blocks: [], type: 'single' };
        }

        if (adjacent.length === 1) {
            return { blocks: adjacent, type: 'single' };
        }

        if (adjacent.length === 2) {
            // Check if they form an L (corner) or a line
            const pos1 = getBlockCenter(adjacent[0], blockSize);
            const pos2 = getBlockCenter(adjacent[1], blockSize);

            const sameX = Math.abs(pos1.x - pos2.x) < blockSize / 2;
            const sameY = Math.abs(pos1.y - pos2.y) < blockSize / 2;

            if (sameX || sameY) {
                return { blocks: adjacent, type: 'line' };
            } else {
                return { blocks: adjacent, type: 'L' };
            }
        }

        // 3 or more blocks - definitely an L or more complex shape
        return { blocks: adjacent, type: 'L' };
    }
};

// Helper function to get block center
function getBlockCenter(block: Location | AtlasMapBlock, defaultSize: number): { x: number; y: number } {
    if ('canvas_x' in block && block.canvas_x != null) {
        const width = ('canvas_width' in block ? block.canvas_width : ('width' in block ? block.width : defaultSize)) || defaultSize;
        const height = ('canvas_height' in block ? block.canvas_height : ('height' in block ? block.height : defaultSize)) || defaultSize;
        return {
            x: block.canvas_x + width / 2,
            y: (block.canvas_y || 0) + height / 2
        };
    }
    return { x: 0, y: 0 };
}

// Helper to create composite image for img2img
async function createCompositeImage(
    newX: number,
    newY: number,
    adjacentBlocks: (Location | AtlasMapBlock)[]
): Promise<{
    imageUrl: string;
    aspectRatio: string;
    newBlockPosition: { x: number; y: number; width: number; height: number };
} | null> {
    if (adjacentBlocks.length === 0) return null;

    // For single block, create 2:1 or 1:2 composite
    if (adjacentBlocks.length === 1) {
        const block = adjacentBlocks[0];
        const blockUrl = getBlockImageUrl(block);
        if (!blockUrl) return null;

        const blockPos = getBlockCenter(block, BLOCK_SIZE);
        const dx = newX + BLOCK_SIZE / 2 - blockPos.x;
        const dy = newY + BLOCK_SIZE / 2 - blockPos.y;

        // Determine if horizontal or vertical expansion
        if (Math.abs(dx) > Math.abs(dy)) {
            // Horizontal (use 3:2 as closest valid aspect ratio)
            const isRight = dx > 0;
            const composite = await createHorizontalComposite(blockUrl, isRight);
            return {
                imageUrl: composite,
                aspectRatio: '3:2',
                newBlockPosition: {
                    x: isRight ? BLOCK_SIZE : 0,
                    y: 0,
                    width: BLOCK_SIZE,
                    height: BLOCK_SIZE
                }
            };
        } else {
            // Vertical (use 2:3 as closest valid aspect ratio)
            const isBelow = dy > 0;
            const composite = await createVerticalComposite(blockUrl, isBelow);
            return {
                imageUrl: composite,
                aspectRatio: '2:3',
                newBlockPosition: {
                    x: 0,
                    y: isBelow ? BLOCK_SIZE : 0,
                    width: BLOCK_SIZE,
                    height: BLOCK_SIZE
                }
            };
        }
    }

    // For L-shape (2-3 blocks), create 2:2 composite
    const composite = await createQuadComposite(newX, newY, adjacentBlocks);
    return {
        imageUrl: composite,
        aspectRatio: '1:1',
        newBlockPosition: {
            x: 0,
            y: 0,
            width: BLOCK_SIZE * 2,
            height: BLOCK_SIZE * 2
        }
    };
}

// Get image URL from block or location
function getBlockImageUrl(block: Location | AtlasMapBlock): string | null {
    if ('atlas_image_url' in block && block.atlas_image_url) {
        return block.atlas_image_url;
    }
    if ('image_url' in block && block.image_url) {
        return block.image_url;
    }
    return null;
}

// Create horizontal 2:1 composite
async function createHorizontalComposite(existingUrl: string, newOnRight: boolean): Promise<string> {
    const canvas = document.createElement('canvas');
    canvas.width = BLOCK_SIZE * 2;
    canvas.height = BLOCK_SIZE;
    const ctx = canvas.getContext('2d')!;

    // Fill with black (empty space)
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Load and draw existing image
    const img = await loadImage(existingUrl);
    if (newOnRight) {
        ctx.drawImage(img, 0, 0, BLOCK_SIZE, BLOCK_SIZE);
    } else {
        ctx.drawImage(img, BLOCK_SIZE, 0, BLOCK_SIZE, BLOCK_SIZE);
    }

    return canvas.toDataURL('image/png');
}

// Create vertical 1:2 composite
async function createVerticalComposite(existingUrl: string, newBelow: boolean): Promise<string> {
    const canvas = document.createElement('canvas');
    canvas.width = BLOCK_SIZE;
    canvas.height = BLOCK_SIZE * 2;
    const ctx = canvas.getContext('2d')!;

    // Fill with black
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Load and draw existing image
    const img = await loadImage(existingUrl);
    if (newBelow) {
        ctx.drawImage(img, 0, 0, BLOCK_SIZE, BLOCK_SIZE);
    } else {
        ctx.drawImage(img, 0, BLOCK_SIZE, BLOCK_SIZE, BLOCK_SIZE);
    }

    return canvas.toDataURL('image/png');
}

// Create 2x2 quad composite for L-shape
async function createQuadComposite(
    newX: number,
    newY: number,
    blocks: (Location | AtlasMapBlock)[]
): Promise<string> {
    const canvas = document.createElement('canvas');
    canvas.width = BLOCK_SIZE * 2;
    canvas.height = BLOCK_SIZE * 2;
    const ctx = canvas.getContext('2d')!;

    // Fill with black
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Determine grid positions for each block
    const centerX = newX + BLOCK_SIZE / 2;
    const centerY = newY + BLOCK_SIZE / 2;

    for (const block of blocks) {
        const url = getBlockImageUrl(block);
        if (!url) continue;

        const pos = getBlockCenter(block, BLOCK_SIZE);
        const img = await loadImage(url);

        // Calculate relative position in the 2x2 grid
        let gridX = pos.x < centerX ? 0 : BLOCK_SIZE;
        let gridY = pos.y < centerY ? 0 : BLOCK_SIZE;

        ctx.drawImage(img, gridX, gridY, BLOCK_SIZE, BLOCK_SIZE);
    }

    return canvas.toDataURL('image/png');
}

// Load image helper
function loadImage(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
    });
}

// Extract new block from composite result
async function extractBlockFromComposite(
    compositeUrl: string,
    position: { x: number; y: number; width: number; height: number },
    userId: string,
    ideaId: string
): Promise<string | null> {
    try {
        const img = await loadImage(compositeUrl);
        const canvas = document.createElement('canvas');
        canvas.width = BLOCK_SIZE;
        canvas.height = BLOCK_SIZE;
        const ctx = canvas.getContext('2d')!;

        // Extract the new block portion
        ctx.drawImage(
            img,
            position.x, position.y, BLOCK_SIZE, BLOCK_SIZE,
            0, 0, BLOCK_SIZE, BLOCK_SIZE
        );

        // Convert to blob and upload
        const blob = await new Promise<Blob>((resolve) => {
            canvas.toBlob((b) => resolve(b!), 'image/png');
        });

        const file = new File([blob], `block_${Date.now()}.png`, { type: 'image/png' });
        const url = await uploadToR2(file, `atlas/${userId}/blocks`, userId);

        return url;
    } catch (error) {
        console.error('Error extracting block from composite:', error);
        return null;
    }
}
